import { useEffect, useState } from 'react';
import { useI18n } from '../i18n';

/**
 * The Windows application's status line: online or offline, what is waiting
 * to be sent, when the last sync was, how many days the offline licence has
 * left, and conflicts to look at. Rendered only inside the desktop shell
 * (`window.oxyDesktop`), where `/api/local/sync/status` exists.
 */
interface SyncStatus {
  mode: 'online' | 'offline' | 'signed_out';
  pending: number;
  conflicts: number;
  recovery: number;
  ruled?: number;
  lastSyncAt: number | null;
  lastError: string | null;
  snapshotDone: boolean;
  syncing: boolean;
  lease: { expiresAt: number | null; daysRemaining: number | null; level: 'none' | 'info' | 'warn' | 'critical' | 'expired' | 'missing' | 'invalid'; detail: string | null };
}

declare global {
  interface Window {
    oxyDesktop?: { isDesktop: true };
  }
}

export const isDesktop = (): boolean => typeof window !== 'undefined' && !!window.oxyDesktop?.isDesktop;

export function DesktopBar() {
  const { t, locale, formatDate } = useI18n();
  const [status, setStatus] = useState<SyncStatus | null>(null);
  const [busy, setBusy] = useState(false);
  const [attention, setAttention] = useState(0);

  // The bar is a fixed strip at the bottom; the shell leaves room for it.
  useEffect(() => {
    document.body.classList.add('is-desktop');
    return () => document.body.classList.remove('is-desktop');
  }, []);

  useEffect(() => {
    let alive = true;
    const load = () =>
      fetch('/api/local/sync/status')
        .then((r) => r.json())
        .then((s: SyncStatus) => {
          if (alive) setStatus(s);
        })
        .catch(() => undefined);
    void load();
    const id = setInterval(load, 5000);
    // Open notices from offline work: through the gateway, so the last
    // answer is shown even without the server.
    const summary = () =>
      fetch('/api/sync/notices/summary')
        .then((r) => (r.ok ? r.json() : null))
        .then((s: { attention?: number } | null) => {
          if (alive && s) setAttention(Number(s.attention ?? 0));
        })
        .catch(() => undefined);
    void summary();
    const id2 = setInterval(summary, 30_000);
    return () => {
      alive = false;
      clearInterval(id);
      clearInterval(id2);
    };
  }, []);

  if (!status || status.mode === 'signed_out') return null;
  const d = t.desktop;
  const offline = status.mode === 'offline';
  const lease = status.lease;
  const leaseText =
    lease.level === 'missing' ? d.leaseMissing
    : lease.level === 'expired' ? d.leaseExpired
    : lease.level === 'invalid' ? d.leaseInvalid
    : lease.daysRemaining !== null ? d.leaseDays.replace('{n}', String(lease.daysRemaining)) : '';

  async function syncNow() {
    setBusy(true);
    try {
      await fetch('/api/local/sync/now', { method: 'POST' });
      const s = (await (await fetch('/api/local/sync/status')).json()) as SyncStatus;
      setStatus(s);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className={`desktop-bar ${offline ? 'is-offline' : 'is-online'}`} role="status">
      <span className="desktop-dot" aria-hidden="true" />
      <strong>{offline ? d.offline : d.online}</strong>
      {offline ? <span className="desktop-note">{d.offlineNote}</span> : null}
      <span className="desktop-sep" />
      <span>{status.pending > 0 ? d.pending.replace('{n}', String(status.pending)) : d.allSent}</span>
      {status.conflicts + status.recovery > 0 ? <span className="desktop-warn">{d.conflicts.replace('{n}', String(status.conflicts + status.recovery))}</span> : null}
      {attention + (status.ruled ?? 0) > 0 ? (
        <button type="button" className="btn btn-ghost btn-sm desktop-warn" onClick={() => window.dispatchEvent(new CustomEvent('oxy:navigate', { detail: 'sync' }))}>
          {d.toLookAt.replace('{n}', String(attention + (status.ruled ?? 0)))}
        </button>
      ) : null}
      <span className="desktop-sep" />
      <span>{status.lastSyncAt ? d.lastSync.replace('{at}', `${formatDate(new Date(status.lastSyncAt))} ${new Date(status.lastSyncAt).toLocaleTimeString(locale === 'ar' ? 'ar' : 'en-GB', { hour: '2-digit', minute: '2-digit' })}`) : d.neverSynced}</span>
      <span className="desktop-sep" />
      <span className={`desktop-lease lease-${lease.level}`}>{leaseText}</span>
      <span className="desktop-grow" />
      {status.lastError && status.lastError !== 'offline' ? <span className="desktop-warn" title={status.lastError}>{d.syncProblem}</span> : null}
      <button type="button" className="btn btn-ghost btn-sm" onClick={() => void syncNow()} disabled={busy || status.syncing}>
        {busy || status.syncing ? d.syncing : d.syncNow}
      </button>
    </div>
  );
}
